export interface PixelCrop {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Given a crop region (in source pixels) and a maximum longest-edge dimension,
 * compute the output canvas size, downscaling proportionally when the crop is
 * larger than maxDim. Pure function so it can be unit tested without a DOM.
 */
export function outputSize(crop: Pick<PixelCrop, 'width' | 'height'>, maxDim: number): { width: number; height: number } {
  const longest = Math.max(crop.width, crop.height)
  const scale = longest > maxDim ? maxDim / longest : 1
  return {
    width: Math.max(1, Math.round(crop.width * scale)),
    height: Math.max(1, Math.round(crop.height * scale)),
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Could not load image'))
    img.src = src
  })
}

/**
 * Crop `imageSrc` to `pixelCrop` (source-pixel coordinates from react-easy-crop),
 * downscale to fit `maxDim`, and return a WebP File ready to upload.
 */
export async function getCroppedFile(
  imageSrc: string,
  pixelCrop: PixelCrop,
  maxDim: number,
  fileName: string,
): Promise<File> {
  const img = await loadImage(imageSrc)
  const { width, height } = outputSize(pixelCrop, maxDim)

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Could not get canvas context')

  ctx.drawImage(
    img,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    width,
    height,
  )

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/webp', 0.88),
  )
  if (!blob) throw new Error('Crop failed')

  const name = fileName.replace(/\.[^.]+$/, '') + '.webp'
  return new File([blob], name, { type: 'image/webp' })
}
